import {Database} from '@tnc/shared';
import {useState} from 'react';

import Page from '@/components/Page';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';
import Kanbanboard from '@/components/kanbanBoard/kanbanBoard';

import {useOrder} from '../providers/orderDetailsProvider';
import ManifestKanbanTask from './manifestKanbanTask';

/**
 * Order Details Component - Displays the order details
 * @returns {JSX.Element}
 */
function OrderDetails() {
  const {order, isOrderLoading, orderId, orderItems, addMetrcTransferId, transfers} = useOrder();
  const [transferId, setTransferId] = useState('');

  const addTransferId = () => {
    addMetrcTransferId(transferId);
    setTransferId('');
  };

  const print = () => {
    window.print();
  };

  return (
    <Page currentPage="Orders" isLoading={isOrderLoading}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-12">
          <div className="box">
            <div className="box-header block md:flex">
              <div className="h5 mb-0 block items-center sm:flex">
                <div>
                  <img src={'/images/logo/logo.png'} alt="" className="w-10" />
                </div>
                <div className="ms-0 mt-2 sm:ms-2 sm:mt-0">
                  <div className="h6 mb-0 font-semibold">
                    Order #: <span className="text-primary">{orderId}</span>
                    <span
                      className={`badge ml-2 inline-flex w-fit !rounded-sm ${
                        order?.order_status === 'pending'
                          ? 'bg-info/10 text-info'
                          : order?.order_status === 'in-progress'
                            ? 'bg-primary/10 text-primary'
                            : order?.order_status === 'accepted'
                              ? 'bg-green-100/10 text-green-500'
                              : order?.order_status === 'rejected'
                                ? 'bg-red-100/10 text-red-500'
                                : order?.order_status === 'draft'
                                  ? 'bg-gray-100/10 text-gray-500'
                                  : 'bg-primary/10 text-primary'
                      }`}
                    >
                      {order?.order_status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="ms-auto mt-2 flex items-center gap-2 space-x-2 md:mt-0">
                <div className="flex items-center">
                  <input
                    type="text"
                    value={transferId}
                    onChange={e => setTransferId(e.target.value)}
                    className="ti-form-control form-control-sm h-full !rounded-r-none"
                    placeholder="Enter Manifest Number"
                  />
                  <button
                    onClick={addTransferId}
                    className="ti-btn ti-btn-primary !m-0 !gap-0 !rounded-l-none !rounded-r-sm !bg-primary !px-2 !py-0.5 !text-[0.75rem] !font-medium !text-white"
                  >
                    Add
                  </button>
                </div>
                <button
                  type="button"
                  className="ti-btn !m-0 h-full bg-secondary !px-2 !py-1 !text-[0.75rem] text-white"
                  onClick={() => print()}
                >
                  Print<i className="ri-printer-line ms-1 inline-block align-middle"></i>
                </button>
                <button type="button" className="ti-btn !m-0 h-full bg-primary !px-2 !py-1 !text-[0.75rem] text-white">
                  Save PDF<i className="ri-file-pdf-line ms-1 inline-block align-middle"></i>
                </button>
              </div>
            </div>
            {/* ORDER ITEMS LIST TABLE */}
            <div className="box-body">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 xl:col-span-12">
                  <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                    <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-4 xl:col-span-4">
                      <p className="mb-2 text-[#8c9097] dark:text-white/50">Distributor:</p>
                      <p className="mb-1 font-bold">{order?.Distributor.distributor_name}</p>
                      <p className="mb-1 text-[#8c9097] dark:text-white/50">{order?.Distributor.distributor_address}</p>
                      <p className="mb-1 text-[#8c9097] dark:text-white/50">
                        {order?.Distributor.distributor_address2}
                      </p>
                      <p className="mb-1 text-[#8c9097] dark:text-white/50">
                        {order?.Distributor.distributor_city}, {order?.Distributor.distributor_state},
                        {order?.Distributor.distributor_postal_code}
                      </p>
                      <p className="mb-1 text-[#8c9097] dark:text-white/50">{order?.Distributor.distributor_email}</p>
                    </div>
                    {order?.order_type === 'Retailer' && (
                      <div className="col-span-12 sm:col-span-6 md:col-span-4 md:col-start-9 lg:col-span-4 xl:col-span-4">
                        <p className="mb-2 text-[#8c9097] dark:text-white/50">Retailer:</p>
                        <p className="mb-1 font-bold">{order?.Retailer.retailer_name}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{order?.Retailer.retailer_address}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{order?.Retailer.retailer_address2}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">
                          {order?.Retailer.retailer_city}, {order?.Retailer.retailer_state},
                          {order?.Retailer.retailer_postal_code}
                        </p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{order?.Retailer.retailer_email}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-12 xl:col-span-12">
                  <div className="table-responsive">
                    <table className="nowrap mt-4 table min-w-full whitespace-nowrap border dark:border-defaultborder/10">
                      <thead>
                        <tr>
                          <th scope="row" className="text-start">
                            PRODUCT
                          </th>
                          <th scope="row" className="text-start">
                            TOTAL
                          </th>
                          <th scope="row" className="text-start">
                            ASSIGNED
                          </th>
                          <th scope="row" className="text-start">
                            UNASSIGNED
                          </th>
                          <th scope="row" className="text-start">
                            MANIFESTS
                          </th>
                          <th scope="row" className="text-start">
                            PACKAGES
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, index) => (
                          <tr key={index} className="border border-defaultborder dark:border-defaultborder/10">
                            <td>
                              {/* <div className="font-semibold">{item.product_name}</div>
                               */}
                              <div className="flex min-w-[150px] gap-2">
                                <DefaultTableAvatarImage
                                  src={item.product_image_url || item.brand_logo_url || '/images/logo/logo.png'}
                                  alt="Product Logo"
                                />
                                <div className="flex flex-col">
                                  <span>{item.product_name}</span>
                                  <span className="text-muted text-truncate text-gray text-xs text-textmuted">
                                    {item.brand_name}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="product-quantity-container text-center">{item.product_count}</td>
                            {/* TODO: ADD LOGIC FOR ASSIGNED AND UNASSIGNED */}
                            <td className="product-quantity-container text-center">{item.metrc_linked_count}</td>
                            <td className="product-quantity-container text-center">
                              {item.product_count - item.metrc_linked_count}
                            </td>
                            <td className="product-quantity-container">
                              <div className="flex flex-col gap-2">
                                {item.packages &&
                                  // TODO:CANT GET ANY OTHER TYPE THAN JSON SO DOING ANYTHING WITH THIS IS NOT POSSIBLE
                                  item.packages.map((pkg: any, index: number) => (
                                    <span
                                      key={index}
                                      className={`badge inline-flex w-fit !rounded-sm bg-primary/10 text-primary`}
                                    >
                                      MANIFEST {index + 1}:{' '}
                                      <span className="pl-1 text-white">{pkg.manifest_number}</span>
                                    </span>
                                  ))}
                              </div>
                            </td>
                            <td className="product-quantity-container">
                              <div className="flex flex-col gap-2">
                                {item.packages &&
                                  item.packages.map((pkg: any, index: number) => (
                                    <span
                                      key={index}
                                      className={`badge inline-flex w-fit !rounded-sm bg-info/10 text-info`}
                                    >
                                      PACKAGE {index + 1}: <span className="pl-1 text-white">{pkg.package_label}</span>
                                    </span>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <Kanbanboard<Database['public']['Functions']['get_metrc_transfers_with_package_counts']['Returns'][number]>
              noHeader
              title={order?.order_type === 'Retailer' ? 'Retailer Orders' : 'Distributor Orders'}
              // @ts-expect-error - temporary ignore
              CustomTaskComponent={ManifestKanbanTask}
              isDark={true}
              columns={[
                {
                  id: 'in-progress',
                  title: 'In Progress',
                  items: transfers.filter(transfer => transfer.received_date_time === null),
                },
                {
                  id: 'received',
                  title: 'Received',
                  items: transfers.filter(
                    transfer =>
                      transfer.received_date_time !== null && transfer.actual_return_arrival_date_time === null
                  ),
                },
                {
                  id: 'rejected',
                  title: 'Rejected',
                  items: transfers.filter(transfer => transfer.actual_return_arrival_date_time !== null),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </Page>
  );
}

export default OrderDetails;
